import { Formik } from "formik";
import * as Yup from "yup";
import {
  Form,
  Select,
  Input,
  InputNumber,
  Button,
  Row,
  Col,
  Space,
  Tooltip,
  Card,
} from "antd";
import { PlusOutlined } from "@ant-design/icons";
import { Package, CheckCircle, Scale, Edit3, Trash2 } from "lucide-react";
import ReusableSearchInput from '@/components/ReusableSearch';
const { TextArea } = Input;

const validationSchema = Yup.object().shape({
  product_id: Yup.string().nullable().required("Product is required"),
  theoretical_value: Yup.number()
    .nullable()
    .required("Theoretical value is required"),
  min_value: Yup.number()
    .nullable()
    .required("Min value is required")
    .test(
      "min-less-than-max",
      "Min value should be less than or equal to max value",
      function (value) {
        const { max_value } = this.parent;
        if (max_value && value) {
          return parseFloat(value) <= parseFloat(max_value);
        }
        return true;
      }
    ),
  max_value: Yup.number()
    .nullable()
    .required("Max value is required")
    .test(
      "max-greater-than-min",
      "Max value should be greater than or equal to min value",
      function (value) {
        const { min_value } = this.parent;
        if (min_value && value) {
          return parseFloat(value) >= parseFloat(min_value);
        }
        return true;
      }
    ),
  instruction: Yup.string().nullable(),
});

export default function Product({
  editIndex,
  onSubmit = (values) => console.log("Submitted values:", values),
  inventory_list,
  values = {
    product_id: null,
    product: null,
    theoretical_value: null,
    min_value: null,
    max_value: null,
    unit: "",
    instruction: null,
  },
}) {
  return (
    <Formik
      initialValues={values}
      validationSchema={validationSchema}
      onSubmit={(vals, { resetForm }) => {
        onSubmit(vals);
        resetForm();
      }}
      enableReinitialize
    >
      {({ values, errors, touched, setFieldValue, handleSubmit }) => (
        <Form layout="vertical" onFinish={handleSubmit}>
          <Row gutter={16}>
            {/* Product Select */}
            <Col span={8}>
              <Form.Item
                label="Product"
                validateStatus={
                  touched.product_id && errors.product_id ? "error" : ""
                }
                help={touched.product_id && errors.product_id}
              >
                <Space.Compact className="w-full">
                  <Select
                    id="product_id"
                    name="product_id"
                    value={values.product_id}
                    onChange={(val, option) => {
                      setFieldValue("product_id", val || null);
                      if (val) {
                        setFieldValue("product", option?.label);
                        setFieldValue("unit", option?.unit);
                      } else {
                        setFieldValue("product", null);
                        setFieldValue("unit", "");
                      }
                    }}
                    placeholder="Select Product"
                    showSearch
                    allowClear
                    optionFilterProp="children"
                    filterOption={(input, option) =>
                      (option?.label ?? "")
                        .toLowerCase()
                        .includes(input.toLowerCase())
                    }
                    options={inventory_list
                      ?.filter((i) => i.material_type === "Product")
                      ?.map((_) => ({
                        value: _?.material_id,
                        label: _?.material_name,
                        unit: _?.unit,
                        unit_id: _?.unit_id,
                      }))}
                  />
                  {/* <Tooltip title="Add Product">
                    <Button icon={<PlusOutlined />} />
                  </Tooltip> */}
                </Space.Compact>
              </Form.Item>
            </Col>

            {/* Theoretical Value */}
            <Col span={6}>
              <Form.Item
                label="Theoretical Value"
                validateStatus={
                  touched.theoretical_value && errors.theoretical_value
                    ? "error"
                    : ""
                }
                help={touched.theoretical_value && errors.theoretical_value}
              >
                <InputNumber
                  className="w-full"
                  placeholder={`Theoretical value ${
                    values.unit ? `in ${values.unit}` : ""
                  }`}
                  min={0}
                  step="any"
                  value={values.theoretical_value}
                  onChange={(val) => setFieldValue("theoretical_value", val)}
                  addonAfter={values.unit}
                />
              </Form.Item>
            </Col>

            {/* Min Value */}
            <Col span={5}>
              <Form.Item
                label="Min Value"
                validateStatus={
                  touched.min_value && errors.min_value ? "error" : ""
                }
                help={touched.min_value && errors.min_value}
              >
                <InputNumber
                  className="w-full"
                  placeholder="Min value"
                  value={values.min_value}
                  onChange={(val) => setFieldValue("min_value", val)}
                  addonAfter={values.unit}
                />
              </Form.Item>
            </Col>

            {/* Max Value */}
            <Col span={5}>
              <Form.Item
                label="Max Value"
                validateStatus={
                  touched.max_value && errors.max_value ? "error" : ""
                }
                help={touched.max_value && errors.max_value}
              >
                <InputNumber
                  className="w-full"
                  placeholder="Max value"
                  value={values.max_value}
                  onChange={(val) => setFieldValue("max_value", val)}
                  addonAfter={values.unit}
                />
              </Form.Item>
            </Col>

            {/* Instruction */}
            <Col span={12}>
              <Form.Item label="Instruction">
                <TextArea
                  rows={2}
                  placeholder="Instruction"
                  value={values.instruction}
                  onChange={(e) => setFieldValue("instruction", e.target.value)}
                />
              </Form.Item>
            </Col>

            {/* Submit */}
            <Col span={24}>
              <Button type="primary" htmlType="submit" block>
                {editIndex !== null ? "Update" : "Add"}
              </Button>
            </Col>
          </Row>
        </Form>
      )}
    </Formik>
  );
}

export const ProductCard = ({ fromView, item, onEdit, onDelete }) => {
  const {
    product,
    product_id,
    theoretical_value,
    min_value,
    max_value,
    unit,
    instruction,
  } = item;

  const showLabel = (label, id) => label || `${id ? "ID: " + id : "N/A"}`;

  return (
    <div className="bg-white dark:bg-slate-900 rounded-lg border border-gray-200 dark:border-slate-700 shadow-sm hover:shadow-md transition p-3 text-sm">
      {/* Header */}
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <div className="p-1.5 bg-green-100 rounded-md">
            <Package className="w-4 h-4 text-green-600" />
          </div>
          <div>
            <p
              className="font-semibold text-gray-900 dark:text-slate-100 leading-tight truncate text-base w-64"
              title={showLabel(product, product_id)}
            >
              {showLabel(product, product_id)}
            </p>
          </div>
        </div>

        {!fromView && (
          <div className="flex items-center gap-1">
            {onEdit && (
              <button
                type="button"
                className="p-1.5 rounded-md hover:bg-blue-50 transition-colors border border-transparent hover:border-blue-200"
                onClick={() => onEdit()}
                title="Edit product"
              >
                <Edit3 className="w-4 h-4 text-blue-600" />
              </button>
            )}
            {onDelete && (
              <button
                type="button"
                className="p-1.5 rounded-md hover:bg-red-50 transition-colors border border-transparent hover:border-red-200"
                onClick={() => onDelete()}
                title="Delete product"
              >
                <Trash2 className="w-4 h-4 text-red-500" />
              </button>
            )}
          </div>
        )}
      </div>

      {/* Content */}
      <div className="grid grid-cols-2 gap-3">
        <div className="bg-blue-50 rounded-md p-2.5">
          <p className="text-xs text-blue-700 font-medium mb-1">
            Theoretical Value
          </p>
          <p className="text-blue-900 font-semibold">
            {theoretical_value ?? "N/A"}{" "}
            <span className="text-sm font-normal text-blue-700">
              {unit || ""}
            </span>
          </p>
        </div>

        {(min_value !== null || max_value !== null) && (
          <div className="bg-orange-50 rounded-md p-2.5">
            <p className="text-xs text-orange-700 font-medium mb-1">Range</p>
            <p className="text-orange-900 font-semibold">
              {min_value ?? "N/A"} - {max_value ?? "N/A"}{" "}
              <span className="text-sm font-normal text-orange-700">
                {unit || ""}
              </span>
            </p>
          </div>
        )}
      </div>

      {/* Instruction */}
      {instruction && (
        <div className="mt-3 bg-gray-50 dark:bg-slate-800/60 rounded-md p-2.5">
          <p className="text-xs text-gray-600 dark:text-slate-300 font-medium mb-1">Instruction</p>
          <p className="text-gray-800 dark:text-slate-200 text-sm leading-relaxed">{instruction}</p>
        </div>
      )}
    </div>
  );
};
